import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { addIcons } from 'ionicons';

import { Device } from '@capacitor/device';
import { SplashScreen } from '@capacitor/splash-screen';

import { Storage } from '@ionic/storage-angular';

import {
  IonApp,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonRouterOutlet,
  IonToolbar,
  Platform,
} from '@ionic/angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  bagOutline,
  businessOutline,
  help,
  homeOutline,
  languageOutline,
  list,
  receiptOutline,
  serverOutline,
  logIn,
  logOut,
  contrastOutline,
  moonOutline,
  sunnyOutline,
  person,
  shieldCheckmark,
  checkmarkOutline,
  chevronDownOutline,
  chevronForwardOutline,
  closeOutline,
  heartOutline,
  helpCircleOutline,
  listOutline,
  logInOutline,
  logOutOutline,
  personCircleOutline,
  shieldCheckmarkOutline,
  mailOutline,
} from 'ionicons/icons';
import { ADMIN_PAGES } from './admin-pages';
import { AdminFabComponent } from './components/admin-fab/admin-fab';
import {
  LANGUAGES,
  SUPPORTED_LANG_CODES,
  toLangCode,
} from './providers/language';
import { SeoService } from './providers/seo.service';
import { AnalyticsService } from './providers/analytics.service';
import { SiteSettingsService } from './providers/site-settings.service';
import { environment } from '../environments/environment';
import { AuthService } from './providers/auth.service';
import { OrderAlertService } from './providers/order-alert.service';
import { SocialLoginService } from './providers/social-login.service';
import { UserService } from './providers/user.service';

import { ThemeMode, ThemeService } from './providers/theme.service';
import { clearStaleBuildReload } from './providers/stale-build';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterLink,
    RouterLinkActive,
    IonRouterOutlet,
    IonLabel,
    IonIcon,
    IonMenuToggle,
    IonList,
    IonItem,
    IonContent,
    IonMenu,
    IonApp,
    TranslatePipe,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonFooter,
    AdminFabComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  private storage = inject(Storage);
  private userService = inject(UserService);
  private auth = inject(AuthService);
  private socialLogin = inject(SocialLoginService);
  // Instantiated here so the admin's live new-order alert starts with the app.
  private orderAlerts = inject(OrderAlertService);
  private swUpdate = inject(SwUpdate);
  private platform = inject(Platform);
  private translate = inject(TranslateService);
  private seo = inject(SeoService);
  private analytics = inject(AnalyticsService);
  /** The brand name shown at the top of the drawer; admin-editable on P-S7N2. */
  protected readonly siteSettings = inject(SiteSettingsService);

  /**
   * The website-builder product — the menu's first section: the product pages
   * that are tabs (Home, Enterprise, Pricing), then those that are not
   * (Integrations, Templates, Use cases). Support, the fourth tab, is under
   * Resources, not Product. Each is a title over a one-line description, the way
   * n8n's own product menu reads: `nav.<key>` over `menu.<key>Desc`.
   */
  productPages = [
    { key: 'home', url: '/', icon: homeOutline, tone: '#fd8925' },
    {
      key: 'shop',
      url: '/shop',
      icon: bagOutline,
      tone: '#22c55e',
    },
    {
      key: 'enterprise',
      url: '/enterprise',
      icon: businessOutline,
      tone: '#ec4899',
    },
    {
      key: 'contact',
      url: '/contact',
      icon: mailOutline,
      tone: '#06b6d4',
    },
  ];

  /**
   * The pages inside the drawer's expandable Admin group (only rendered when
   * `isAdmin()`): Inventory, Users, Orders, Settings — each guarded the same way
   * `/admin` (now Inventory) already was, by `requireLoginGuard`.
   */
  adminPages = ADMIN_PAGES;

  /** The menu's other icons, bound with [icon] (no name registry needed). */
  readonly menuIcons = {
    close: closeOutline,
    chevron: chevronDownOutline,
    forward: chevronForwardOutline,
    help: helpCircleOutline,
    requests: listOutline,
    dashboard: shieldCheckmarkOutline,
    logout: logOutOutline,
    login: logInOutline,
    account: personCircleOutline,
    orders: receiptOutline,
    wishlist: heartOutline,
    language: languageOutline,
    check: checkmarkOutline,
    system: contrastOutline,
    light: sunnyOutline,
    dark: moonOutline,
  };
  languages = LANGUAGES;
  // Seeded from the session the app initializer already restored, so the menu
  // renders in its final shape on the first paint instead of flipping from the
  // signed-out list once storage answers. `checkLoginStatus()` still runs and
  // reconciles, which is what keeps the local flag authoritative.
  loggedIn = signal(this.auth.isAuthenticated());
  /** Gates the drawer's Admin group — the API re-checks this on every request. */
  readonly isAdmin = this.auth.isAdmin;
  /** The drawer's expandable Admin group; opens on its own inside /admin/*. */
  readonly adminOpen = signal(this.router.url.startsWith('/admin'));
  /** True while the current page is one of the admin pages. */
  readonly adminRouteActive = signal(this.router.url.startsWith('/admin'));

  /**
   * Which build this is — `prod` for a production build, `dev` otherwise.
   * Surfaced in the menu because the two builds differ only in the backend
   * they point at, which is otherwise invisible from inside the app. The
   * endpoint itself is deliberately not shown.
   */
  readonly environmentName = environment.production ? 'prod' : 'dev';
  username = signal('');
  currentLang = signal('en');
  /** The language panel in the drawer footer, collapsed until tapped. */
  readonly langOpen = signal(false);
  readonly currentLanguage = computed(
    () =>
      this.languages.find(l => l.code === this.currentLang()) ??
      this.languages[0],
  );
  /**
   * Each language's name in the reader's current language ("German" when
   * reading English, "Duits" in Dutch), shown under its own name so a
   * reader can find their way back from a language they can't read. Comes
   * from the browser's Intl data, so no translation keys are needed; empty
   * when it would repeat the native label.
   */
  readonly languageHints = computed(() => {
    let names: Intl.DisplayNames | undefined;
    try {
      names = new Intl.DisplayNames([this.currentLang()], { type: 'language' });
    } catch {
      // Older engines without Intl.DisplayNames: show native names only.
    }
    const hints: Record<string, string> = {};
    for (const l of this.languages) {
      const name = names?.of(l.code) ?? '';
      hints[l.code] = name.toLowerCase() === l.label.toLowerCase() ? '' : name;
    }
    return hints;
  });
  /** The colour theme — see ThemeService; the template reads it too. */
  readonly theme = inject(ThemeService);
  readonly themeModes: ThemeMode[] = ['light', 'dark'];

  constructor() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        // A page loaded, so this build works: allow another stale-build reload.
        clearStaleBuildReload();
        const inAdmin = this.router.url.startsWith('/admin');
        this.adminRouteActive.set(inAdmin);
        if (inAdmin) {
          this.adminOpen.set(true);
        }
      });
    // Icons other templates still name as strings (the menu itself binds
    // SVGs with [icon]).
    addIcons({
      homeOutline,
      person,
      help,
      list,
      shieldCheckmark,
      logOut,
      logIn,
      contrastOutline,
      moonOutline,
      sunnyOutline,
      languageOutline,
      serverOutline,
      businessOutline,
      mailOutline,
    });
  }

  async ngOnInit() {
    // Before any await: the first navigation can finish while storage opens,
    // and a tracker started after it would miss the landing page's view.
    this.analytics.init();
    this.initializeApp();
    // Theme on <html> as well as <ion-app>, so overlays rendered outside
    // <ion-app> (toasts, popovers) match; index.html has already painted it.
    this.theme.init();
    await this.storage.create();
    // Rehydrate the API token before anything can make an authenticated call,
    // so a reload does not silently sign the user out of the admin area.
    await this.auth.restore();
    // Fire-and-forget: nothing needs the native Google/Facebook SDKs ready
    // before the app renders, only before someone taps a sign-in button.
    void this.socialLogin.initialize();
    await this.initTranslate();
    // Start managing per-route document SEO (title, meta, canonical, OG).
    this.seo.init();
    this.checkLoginStatus();
    this.listenForLoginEvents();

    // No "Update available" prompt: the service worker downloads a new build
    // in the background and serves it on the next load. Only a broken cache
    // (the old build's files are gone from the server) needs a reload now.
    if (this.swUpdate.isEnabled) {
      this.swUpdate.unrecoverable.subscribe(() => window.location.reload());
    }
  }

  initializeApp() {
    this.platform.ready().then(() => {
      if (this.platform.is('hybrid')) {
        // The status bar stays visible; ThemeService keeps its colours in
        // step with the theme.
        SplashScreen.hide();
      }
    });
  }

  checkLoginStatus() {
    return this.userService.isLoggedIn().then(loggedIn => {
      return this.updateLoggedInStatus(loggedIn);
    });
  }

  updateLoggedInStatus(loggedIn: boolean) {
    setTimeout(() => {
      this.loggedIn.set(loggedIn);
      if (loggedIn) {
        this.userService
          .getUsername()
          .then(username => this.username.set(username ?? ''));
      } else {
        this.username.set('');
      }
    }, 300);
  }

  // Initialise translations: register languages, then pick — in priority
  // order — the user's saved choice, the client device language, or English.
  private async initTranslate() {
    this.translate.addLangs(SUPPORTED_LANG_CODES);
    this.translate.setFallbackLang('en');

    const saved = (await this.storage.get('app_language')) as string | null;
    const device = await this.detectDeviceLanguage();
    const initial =
      saved && SUPPORTED_LANG_CODES.includes(saved)
        ? saved
        : device && SUPPORTED_LANG_CODES.includes(device)
          ? device
          : 'en';

    this.translate.use(initial);
    this.currentLang.set(initial);
  }

  // Resolve the client device's language as a 2-letter code (e.g. 'fr').
  // On native (iOS/Android) `navigator.language` inside the Capacitor WebView
  // does not reliably reflect the OS language, so read it from the Device
  // plugin; on the web fall back to the browser language.
  private async detectDeviceLanguage(): Promise<string | undefined> {
    let lang: string | undefined;
    try {
      if (this.platform.is('hybrid')) {
        const { value } = await Device.getLanguageCode();
        lang = value;
      }
    } catch {
      // Ignore and fall back to the browser language below.
    }
    // Fall back to the browser language on the web, or when the plugin
    // returned nothing usable (null/undefined/empty). Use ||= so an empty
    // string from the plugin also falls through.
    lang ||= navigator.language;
    return toLangCode(lang);
  }

  // Called from the menu language selector.
  setLanguage(lang: string) {
    this.langOpen.set(false);
    this.translate.use(lang);
    this.currentLang.set(lang);
    this.storage.set('app_language', lang);
  }

  // Tapping the menu avatar always navigates to home.
  openAccount() {
    this.router.navigateByUrl('/');
  }

  listenForLoginEvents() {
    window.addEventListener('user:login', () => {
      this.updateLoggedInStatus(true);
    });

    window.addEventListener('user:signup', () => {
      this.updateLoggedInStatus(true);
    });

    window.addEventListener('user:logout', () => {
      this.updateLoggedInStatus(false);
    });
  }

  logout() {
    // Drop the API token as well as the local session flag, or the next login
    // would inherit the previous user's credential.
    Promise.all([this.userService.logout(), this.auth.logout()]).then(() => {
      return this.router.navigateByUrl('/');
    });
  }
}
